/**
 * Centralized recovery-record storage for the auth `invalid_grant` flow.
 *
 * Both the runtime path (`useInvalidGrantHandler`) and the callback path
 * (`useSessionRecovery`) read/write the same record so recovery attempts stay
 * coordinated. Without this, the runtime and callback paths can each kick off
 * their own retry chain and recreate the redirect loop from issue #567.
 *
 * Storage uses a single JSON blob in sessionStorage under
 * `meetball_auth_recovery`. On native, writes also mirror into Capacitor
 * Preferences so the record survives the WebView kill that can happen while
 * the system browser owns the foreground during OAuth.
 *
 * Records expire after `RECOVERY_TTL_MS` (currently 5 minutes) measured from
 * `startedAt`. An expired record is treated as absent — the next `invalid_grant`
 * starts a fresh recovery cycle.
 */

import { isNativePlatform } from '@lib/platform';
import { logger } from '@utils/logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Recovery record kinds form two parallel channels stored in the same blob:
 *
 *   1. The recovery FLOW: linear progression of re-auth attempts.
 *      `fresh` → `silent-attempted` → `full-initiated` → `terminal`
 *
 *   2. The BREAKER signal (background, single state): the token-refresh
 *      circuit breaker tripped on non-grant errors. Persisted so other
 *      components can observe the same stop signal cross-tab/cross-component,
 *      but it is NOT terminal for the recovery flow — a real `invalid_grant`
 *      arriving while we're in `breaker-tripped` overrides it back into a
 *      fresh recovery cycle.
 *
 *      Why the channel split exists: conflating these two channels via a
 *      single `terminal` kind caused a recovery-poisoning bug where a
 *      transient backend blip (Logto 503, network outage) tripped the
 *      breaker, wrote `terminal(max-attempts)`, and blocked silent re-auth
 *      on the next genuine session expiry until the record's TTL window
 *      expired. Splitting `breaker-tripped` into its own kind (with
 *      `isRecoveryTerminal` returning false for it) decouples the channels
 *      and lets recovery-flow patches override the breaker signal cleanly.
 */
export type RecoveryRecord =
  | { kind: 'fresh'; startedAt: number }
  | { kind: 'silent-attempted'; startedAt: number; lastAttemptAt: number }
  | { kind: 'full-initiated'; startedAt: number; lastAttemptAt: number }
  | {
      kind: 'terminal';
      startedAt: number;
      reason: 'max-attempts' | 'explicit';
      lastAttemptAt: number;
    }
  | { kind: 'breaker-tripped'; startedAt: number; lastTrippedAt: number };

/**
 * Kinds that participate in the linear recovery flow's stage tracking.
 *
 * Excludes `breaker-tripped`, which is the out-of-band signal channel from
 * the token-refresh circuit breaker — it must NEVER reach `runWebAttempts`
 * or `runNativeAttempt`'s `currentKind` branches. `handleInvalidGrant`
 * filters it out at the boundary via `getRecoveryFlowRecord`, but accepting
 * only `RecoveryFlowKind` (instead of the full `RecoveryRecord['kind']`)
 * at function signatures lets TypeScript catch any future caller that
 * forgets to filter, before the runtime poisoning bug can re-emerge.
 *
 * **Defined as a positive enumeration (not `Exclude<>`) on purpose.** Adding
 * a new variant to `RecoveryRecord` must force a deliberate flow-vs-signal
 * decision: a flow kind goes here, a signal kind does not. `Exclude<>` would
 * silently widen this type to include any new flow-shaped kind without any
 * review prompt — the same omission class that originally caused the
 * recovery-poisoning bug. The closed enumeration's compile failure on
 * `RecoveryRecord` evolution is the friction we want.
 */
export type RecoveryFlowKind = 'fresh' | 'silent-attempted' | 'full-initiated' | 'terminal';

/**
 * A `RecoveryRecord` narrowed to the flow-channel kinds — i.e. the discriminated
 * union with the out-of-band `breaker-tripped` variant excluded. Use this as
 * the type for any value that has been (or must be) filtered through
 * `getRecoveryFlowRecord` before it reaches recovery-flow stage tracking.
 */
export type RecoveryFlowRecord = Extract<RecoveryRecord, { kind: RecoveryFlowKind }>;

/**
 * Single source of truth for "treat `breaker-tripped` as no recovery state."
 *
 * The recovery flow's stage tracking (`runWebAttempts` / `runNativeAttempt`)
 * must never see a `breaker-tripped` record — it would silently fall through
 * the `currentKind` branches and short-circuit to the terminal drawer,
 * recreating the recovery-poisoning bug this whole channel split exists to
 * prevent. Both `handleInvalidGrant` (handler boundary) and `computeNextRecord`
 * (write transitions for non-`BREAKER_TRIPPED` patches) call this helper so
 * the invariant has exactly one definition.
 *
 * @returns The record unchanged if it is a flow kind; `null` if the record is
 *          `null` or `breaker-tripped`. Reference equality is preserved on
 *          pass-through so callers can compare identity if needed.
 */
export function getRecoveryFlowRecord(record: RecoveryRecord | null): RecoveryFlowRecord | null {
  if (record === null || record.kind === 'breaker-tripped') {
    return null;
  }
  return record;
}

interface LegacyRecoveryRecord {
  startedAt: number;
  attempts: number;
  lastAttemptType: 'silent' | 'full' | null;
  terminal: boolean;
}

/**
 * Semantic state transitions for the shared recovery record.
 *
 * `SESSION_CLEARED_FOR_RETRY` is used by the callback-path `/oidc/session/end`
 * recovery. With no prior record it creates the interactive-retry stage; after
 * a silent/full stage it advances to terminal so the next failure stops.
 *
 * `BREAKER_TRIPPED` is the out-of-band signal channel used by
 * `recordTokenRefreshFailure`. It sits OUTSIDE the linear recovery flow:
 *   - Non-BREAKER_TRIPPED transitions treat an incoming `breaker-tripped`
 *     state as if the slot were empty (so a real recovery cycle starting
 *     after a breaker trip gets a fresh `startedAt` and isn't blocked).
 *   - A BREAKER_TRIPPED patch preserves the existing record (the breaker
 *     signal is dropped) when the existing kind is `silent-attempted`,
 *     `full-initiated`, or `terminal` — those reflect actual recovery
 *     decisions and take precedence over the breaker's background signal.
 *     Note that `updateRecoveryRecord` still runs the validation pass and
 *     re-serializes the unchanged record, so this is not a true I/O no-op.
 *   - The patch writes a `breaker-tripped` record when the slot is empty,
 *     `fresh`, or already `breaker-tripped`. Replacing `fresh` is
 *     intentional: a `fresh` cycle has no attempt yet, so the more recent
 *     breaker signal wins. `startedAt` is carried over from the prior
 *     record so the TTL window stays anchored.
 */
export type RecoveryRecordPatch =
  | { type: 'START_CYCLE' }
  | { type: 'SILENT_ATTEMPTED'; at?: number }
  | { type: 'FULL_INITIATED'; at?: number }
  | { type: 'SESSION_CLEARED_FOR_RETRY'; at?: number }
  | { type: 'TERMINAL'; reason: 'max-attempts' | 'explicit'; at?: number }
  | { type: 'BREAKER_TRIPPED'; at?: number };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'meetball_auth_recovery';

/** Window after which a record is considered stale and treated as absent. */
const RECOVERY_TTL_MS = 5 * 60 * 1000;

/** Terminal after this many recovery stages. */
export const MAX_RECOVERY_ATTEMPTS = 2;

/**
 * Circuit-breaker thresholds for persistent token-refresh failures.
 *
 * Covers non-`invalid_grant` outages (503 from Logto, network errors, browser
 * request blocking) where the standard recovery flow would never fire. Once
 * the breaker trips, callers escalate to the shared recovery UI instead of
 * spinning forever.
 */
const TOKEN_FAILURE_THRESHOLD = 3;
const TOKEN_FAILURE_WINDOW_MS = 30_000;

// ---------------------------------------------------------------------------
// Validation + migration
// ---------------------------------------------------------------------------

function isValidRecord(value: unknown): value is RecoveryRecord {
  if (typeof value !== 'object' || value === null) return false;

  const obj = value as Record<string, unknown>;
  if (typeof obj.startedAt !== 'number') return false;

  switch (obj.kind) {
    case 'fresh':
      return true;
    case 'silent-attempted':
    case 'full-initiated':
      return typeof obj.lastAttemptAt === 'number';
    case 'terminal':
      return (
        typeof obj.lastAttemptAt === 'number' &&
        (obj.reason === 'max-attempts' || obj.reason === 'explicit')
      );
    case 'breaker-tripped':
      return typeof obj.lastTrippedAt === 'number';
    default:
      return false;
  }
}

function isValidLegacyRecord(value: unknown): value is LegacyRecoveryRecord {
  if (typeof value !== 'object' || value === null) return false;

  const obj = value as Record<string, unknown>;
  if (typeof obj.startedAt !== 'number') return false;
  if (typeof obj.attempts !== 'number') return false;
  if (typeof obj.terminal !== 'boolean') return false;
  if (
    obj.lastAttemptType !== null &&
    obj.lastAttemptType !== 'silent' &&
    obj.lastAttemptType !== 'full'
  ) {
    return false;
  }

  return true;
}

function normalizeLegacyRecord(record: LegacyRecoveryRecord): RecoveryRecord {
  const lastAttemptAt = record.startedAt;

  if (record.terminal) {
    return {
      kind: 'terminal',
      startedAt: record.startedAt,
      reason: 'explicit',
      lastAttemptAt,
    };
  }

  if (record.attempts >= MAX_RECOVERY_ATTEMPTS) {
    return {
      kind: 'terminal',
      startedAt: record.startedAt,
      reason: 'max-attempts',
      lastAttemptAt,
    };
  }

  if (record.attempts === 1 && record.lastAttemptType === 'silent') {
    return {
      kind: 'silent-attempted',
      startedAt: record.startedAt,
      lastAttemptAt,
    };
  }

  if (record.attempts === 1 && record.lastAttemptType === 'full') {
    return {
      kind: 'full-initiated',
      startedAt: record.startedAt,
      lastAttemptAt,
    };
  }

  return { kind: 'fresh', startedAt: record.startedAt };
}

function normalizeRecord(value: unknown): RecoveryRecord | null {
  if (isValidRecord(value)) {
    return value;
  }

  if (isValidLegacyRecord(value)) {
    return normalizeLegacyRecord(value);
  }

  return null;
}

function isExpired(record: RecoveryRecord): boolean {
  return Date.now() - record.startedAt >= RECOVERY_TTL_MS;
}

// ---------------------------------------------------------------------------
// Read (sessionStorage - synchronous)
// ---------------------------------------------------------------------------

/**
 * Read the current recovery record from sessionStorage.
 * Returns null if absent, expired, or corrupt.
 *
 * For native contexts where the WebView may have been killed during a
 * system-browser round-trip, prefer `getRecoveryRecordAsync()` — it falls back
 * to Capacitor Preferences.
 */
export function getRecoveryRecord(): RecoveryRecord | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = normalizeRecord(JSON.parse(raw));
    if (!parsed) return null;
    if (isExpired(parsed)) return null;

    return parsed;
  } catch (err) {
    logger.warn('[authRecovery] Failed to read sessionStorage:', err);
    return null;
  }
}

/**
 * Native-aware read. Checks sessionStorage first, falls back to Capacitor
 * Preferences if sessionStorage is empty (common after a WebView kill during a
 * system-browser OAuth round-trip on iOS/Android).
 */
export async function getRecoveryRecordAsync(): Promise<RecoveryRecord | null> {
  const fromSession = getRecoveryRecord();
  if (fromSession) return fromSession;

  if (!isNativePlatform()) return null;

  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (!value) return null;

    const parsed = normalizeRecord(JSON.parse(value));
    if (!parsed) return null;
    if (isExpired(parsed)) return null;

    return parsed;
  } catch (err) {
    logger.error('[authRecovery] Failed to read native Preferences:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

function computeNextRecord(
  patch: RecoveryRecordPatch,
  preloadedExisting?: RecoveryRecord | null,
): RecoveryRecord {
  // Allow callers on the async write path to seed `existing` from the
  // Preferences-backed read (getRecoveryRecordAsync), which survives a
  // native WebView kill. Sync callers omit the parameter and fall back
  // to the sessionStorage-only read.
  const rawExisting = preloadedExisting !== undefined ? preloadedExisting : getRecoveryRecord();
  const now = patch.type === 'START_CYCLE' ? Date.now() : (patch.at ?? Date.now());

  // Recovery-flow transitions (everything except BREAKER_TRIPPED) treat a
  // `breaker-tripped` existing record as if the slot were empty: the breaker
  // signal sits outside the linear recovery flow, so a new recovery cycle
  // starting after a breaker trip gets its own fresh `startedAt` (otherwise a
  // 4-minute-old breaker-tripped record would shrink the new recovery's TTL
  // window down to 1 minute). `getRecoveryFlowRecord` is the shared
  // single-source-of-truth filter (also used by `handleInvalidGrant`).
  // BREAKER_TRIPPED itself preserves any pre-existing recovery state — see
  // its case below.
  const existing = getRecoveryFlowRecord(rawExisting);
  const startedAt = existing?.startedAt ?? now;

  switch (patch.type) {
    case 'START_CYCLE':
      return existing ?? { kind: 'fresh', startedAt };

    case 'SILENT_ATTEMPTED':
      if (existing?.kind === 'terminal') {
        return existing;
      }

      return {
        kind: 'silent-attempted',
        startedAt,
        lastAttemptAt: now,
      };

    case 'FULL_INITIATED':
      if (existing?.kind === 'terminal') {
        return existing;
      }

      return {
        kind: 'full-initiated',
        startedAt,
        lastAttemptAt: now,
      };

    case 'SESSION_CLEARED_FOR_RETRY':
      if (existing?.kind === 'terminal') {
        return existing;
      }

      if (!existing || existing.kind === 'fresh') {
        return {
          kind: 'full-initiated',
          startedAt,
          lastAttemptAt: now,
        };
      }

      return {
        kind: 'terminal',
        startedAt,
        reason: 'max-attempts',
        lastAttemptAt: now,
      };

    case 'TERMINAL':
      return {
        kind: 'terminal',
        startedAt,
        reason: patch.reason,
        lastAttemptAt: now,
      };

    case 'BREAKER_TRIPPED':
      // Single emitter: `recordTokenRefreshFailure`. Do not synthesize
      // BREAKER_TRIPPED from any other call site — the patch type is exported
      // so the test suite can drive it directly, but no production code path
      // should construct one outside the breaker callback. `computeNextRecord`'s
      // precedence rules below provide a safety net even if that invariant
      // ever breaks (the breaker can't clobber an in-flight recovery), but
      // bypassing the single-emitter contract would still pollute the record
      // with mixed-source writes that future readers would have to disentangle.
      //
      // The breaker signal must NOT clobber an in-progress recovery flow
      // (silent-attempted, full-initiated) or a `terminal` record. Those
      // states reflect actual recovery decisions; the breaker is just a
      // "we hit a few transient failures" annotation.
      if (rawExisting && rawExisting.kind !== 'breaker-tripped' && rawExisting.kind !== 'fresh') {
        return rawExisting;
      }

      return {
        kind: 'breaker-tripped',
        startedAt: rawExisting?.startedAt ?? now,
        lastTrippedAt: now,
      };
  }
}

function writeSessionStorageRecord(serialized: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, serialized);
  } catch (err) {
    logger.error('[authRecovery] Failed to write sessionStorage:', err);
  }
}

/**
 * Create or advance the recovery record using a semantic state transition.
 *
 * - If no record exists (or the existing record is expired), `startedAt` is
 *   set to now.
 * - If a record already exists, the transition preserves `startedAt` so the
 *   TTL continues to bound the overall cycle.
 *
 * Always writes to sessionStorage. On native, also mirrors to Capacitor
 * Preferences **fire-and-forget**; if the caller needs durability across a
 * WebView kill (e.g. right before launching a system-browser OAuth flow), use
 * `updateRecoveryRecordAsync` instead so the Preferences write is awaited.
 */
export function updateRecoveryRecord(patch: RecoveryRecordPatch): RecoveryRecord {
  const next = computeNextRecord(patch);
  const serialized = JSON.stringify(next);

  writeSessionStorageRecord(serialized);

  if (isNativePlatform()) {
    void writeNativePreferences(serialized);
  }

  return next;
}

/**
 * Same as `updateRecoveryRecord` but awaits the Capacitor Preferences write on
 * native. Callers that update the record immediately before `logtoSignIn()`
 * on native must use this variant; otherwise the WebView can be
 * backgrounded/killed during the system-browser round-trip before the
 * fire-and-forget Preferences write has flushed. That would defeat the
 * record's whole purpose of surviving a WebView kill.
 *
 * On web this is equivalent to `updateRecoveryRecord` (the native branch is
 * skipped).
 */
export async function updateRecoveryRecordAsync(
  patch: RecoveryRecordPatch,
): Promise<RecoveryRecord> {
  // Seed from the Preferences-backed read so `startedAt` is preserved
  // across a native WebView kill, where sessionStorage is empty but
  // Capacitor Preferences may still hold the recovery record. Without
  // this, computeNextRecord would see no existing record and reset the
  // TTL-bounding startedAt, also letting SESSION_CLEARED_FOR_RETRY
  // silently skip the terminal-state escalation.
  const existing = await getRecoveryRecordAsync();
  const next = computeNextRecord(patch, existing);
  const serialized = JSON.stringify(next);

  writeSessionStorageRecord(serialized);

  if (isNativePlatform()) {
    await writeNativePreferences(serialized);
  }

  return next;
}

// ---------------------------------------------------------------------------
// Clear
// ---------------------------------------------------------------------------

/** Remove the record from all storage layers. Called on successful auth. */
export function clearRecoveryRecord(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    logger.error('[authRecovery] Failed to clear sessionStorage:', err);
  }

  if (isNativePlatform()) {
    void clearNativePreferences();
  }

  // Successful auth transitions call this; zero the in-memory breaker counter
  // so the next outage starts with a clean window.
  resetTokenRefreshFailures();
}

// ---------------------------------------------------------------------------
// Circuit breaker
// ---------------------------------------------------------------------------

export interface TokenFailureResult {
  /** Consecutive failures recorded in the current window (1-based). */
  count: number;
  /** True when `count` reached `TOKEN_FAILURE_THRESHOLD` on this call. */
  isTerminal: boolean;
}

export interface TokenFailureBreaker {
  record: () => TokenFailureResult;
  reset: () => void;
  isTripped: () => boolean;
}

export function createTokenFailureBreaker(): TokenFailureBreaker {
  let count = 0;
  let firstAt = 0;

  return {
    record: () => {
      const now = Date.now();

      if (firstAt && now - firstAt > TOKEN_FAILURE_WINDOW_MS) {
        count = 1;
        firstAt = now;
      } else {
        count += 1;
        if (!firstAt) {
          firstAt = now;
        }
      }

      return { count, isTerminal: count >= TOKEN_FAILURE_THRESHOLD };
    },
    reset: () => {
      count = 0;
      firstAt = 0;
    },
    isTripped: () => {
      if (!firstAt) {
        return false;
      }

      if (Date.now() - firstAt > TOKEN_FAILURE_WINDOW_MS) {
        return false;
      }

      return count >= TOKEN_FAILURE_THRESHOLD;
    },
  };
}

export const tokenFailureBreaker = createTokenFailureBreaker();

/**
 * Record a token-refresh failure and report whether the breaker has tripped.
 *
 * Call this from any catch block that wraps a token-fetch attempt (excluding
 * the invalid_grant path, which has its own recovery flow). A successful token
 * fetch must call `resetTokenRefreshFailures()` to close the window.
 *
 * When the breaker trips, the caller is expected to escalate to auth recovery.
 * We also persist a `breaker-tripped` recovery record so cross-component
 * readers see the same stop signal — but it is intentionally a DIFFERENT kind
 * from the recovery-flow `terminal` state so a transient outage that trips the
 * breaker doesn't poison the next genuine `invalid_grant`'s recovery attempt
 * (`isRecoveryTerminal` returns false for `breaker-tripped`, and any
 * recovery-flow patch will override `breaker-tripped` back into the flow).
 */
export function recordTokenRefreshFailure(): TokenFailureResult {
  const result = tokenFailureBreaker.record();

  if (result.isTerminal) {
    // Sync `updateRecoveryRecord` (fire-and-forget Preferences mirror on
    // native) is intentional. The breaker record is advisory — the
    // in-memory `tokenFailureBreaker` counter is the actual debounce, and
    // a native Preferences-mirror failure logs but doesn't change behaviour.
    // Callers (`getTokenForApi`, `useProactiveTokenRefresh`) rely on this
    // function being synchronous to consume `result.isTerminal` immediately.
    // Don't upgrade to `updateRecoveryRecordAsync` without updating the
    // native fire-and-forget contract tests in authRecovery.test.ts.
    updateRecoveryRecord({ type: 'BREAKER_TRIPPED' });
  }

  return result;
}

/**
 * Clear the in-memory failure counter. Call after any successful token fetch
 * so transient blips never accumulate toward the breaker.
 */
export function resetTokenRefreshFailures(): void {
  tokenFailureBreaker.reset();
}

// ---------------------------------------------------------------------------
// Terminal check
// ---------------------------------------------------------------------------

/**
 * Whether the current recovery cycle should stop attempting re-auth.
 *
 * A null record (absent or expired past the TTL) is non-terminal so a fresh
 * `invalid_grant` always gets a clean attempt.
 *
 * The optional `record` parameter lets callers pass an already-loaded record
 * (e.g. from `getRecoveryRecordAsync()` on native, which may have restored
 * from Capacitor Preferences after a WebView kill). When omitted, the record
 * is read synchronously from sessionStorage — which on native after a
 * WebView kill is empty, so omitting the argument on that path would
 * incorrectly return false and let a terminal record trigger another
 * recovery cycle.
 */
export function isRecoveryTerminal(record?: RecoveryRecord | null): boolean {
  const effective = record === undefined ? getRecoveryRecord() : record;
  return effective?.kind === 'terminal';
}

// ---------------------------------------------------------------------------
// Internal: Capacitor Preferences mirrors
// ---------------------------------------------------------------------------

async function writeNativePreferences(value: string): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: STORAGE_KEY, value });
  } catch (err) {
    logger.error('[authRecovery] Failed to write native Preferences:', err);
  }
}

async function clearNativePreferences(): Promise<void> {
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key: STORAGE_KEY });
  } catch (err) {
    logger.error('[authRecovery] Failed to clear native Preferences:', err);
  }
}
